// v10/src/components/ReviewModal.js - NEW COMPONENT
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle2, ChevronRight, Eye } from 'lucide-react';

const ReviewModal = ({ 
  pendingItems, 
  currentIndex, 
  onReviewItem, 
  onClose, 
  darkMode 
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const current = pendingItems[currentIndex];
  const totalItems = pendingItems.length;
  const progress = ((currentIndex + 1) / totalItems) * 100;

  if (!current) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 30 }}
        transition={{ type: "spring", duration: 0.5 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        {/* Header con progreso */}
        <div className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 10, 0]
                }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  repeatDelay: 0.5
                }}
                className="text-2xl"
              >
                ⚠️
              </motion.div>
              <div>
                <h2 className="font-bold text-white text-lg">Revisión de Chequeo</h2>
                <p className="text-sm text-white/90">
                  Item {currentIndex + 1} de {totalItems}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors text-2xl"
            >
              ✕
            </button>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
          <p className="text-xs text-white/80 mt-2 text-center font-semibold">
            {progress.toFixed(0)}% completado
          </p>
        </div>

        {/* Content */}
        <div className={`p-6 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* Item a revisar */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`p-4 rounded-2xl border-2 mb-6 ${
              darkMode
                ? 'bg-orange-900/20 border-orange-600'
                : 'bg-orange-50 border-orange-200'
            }`}
          >
            <p className={`text-sm font-semibold ${
              darkMode ? 'text-orange-400' : 'text-orange-600'
            } mb-2`}>
              Pendiente de revisar:
            </p>
            <p className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {current.text}
            </p>
          </motion.div>

          {/* Descripción si existe */}
          {current.description && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className={`p-3 rounded-xl mb-4 text-sm ${
                darkMode
                  ? 'bg-gray-700/50 text-gray-300'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              ℹ️ {current.description}
            </motion.div>
          )}

          {/* Botones de acción */}
          <div className="space-y-3">
            {/* Botón de referencia si existe */}
            {current.referenceImage && (
              <motion.button
                onClick={() => setShowDetails(!showDetails)}
                className={`w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  darkMode
                    ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 border border-blue-600'
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Eye className="w-5 h-5" />
                {showDetails ? 'Ocultar referencia' : 'Ver imagen de referencia'}
              </motion.button>
            )}

            {/* Mostrar imagen si está disponible */}
            <AnimatePresence>
              {showDetails && current.referenceImage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <img
                    src={current.referenceImage}
                    alt={current.text}
                    className="w-full h-48 object-cover rounded-xl mb-3"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Botón de revisar */}
            <motion.button
              onClick={() => onReviewItem(currentIndex)}
              className="w-full py-4 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CheckCircle2 className="w-6 h-6" />
              ✓ Revisar este item
            </motion.button>

            {/* Próximo item preview */}
            {currentIndex < totalItems - 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`p-3 rounded-xl text-center text-sm ${
                  darkMode
                    ? 'bg-gray-700/50 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <p className="flex items-center justify-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  Siguiente: {pendingItems[currentIndex + 1].text}
                </p>
              </motion.div>
            )}
          </div>

          {/* Footer con info */}
          <div className={`mt-4 pt-4 border-t ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <p className={`text-xs text-center ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              🔇 Modo silencioso activado - Sin voz durante esta revisión
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReviewModal;
